/* eslint-disable @typescript-eslint/no-explicit-any */
export * from './color'
export * from './icon'
export * from './helper'

import axios from 'axios'
import { getRandomString } from '@/lib'
export async function waitForCondition(
  condition: () => boolean,
  delay: number = 100,
  timeout: number = 30000, // 기본 30초 timeout
): Promise<void> {
  const startTime = Date.now()
  while (!condition()) {
    if (timeout > 0 && Date.now() - startTime > timeout) {
      throw new Error('waitForCondition timeout exceeded')
    }
    await sleep(delay) // 100밀리초 대기
  }
}

export async function waitForSuccess(action: Promise<void>, interval: number): Promise<void> {
  while (true) {
    try {
      // action이 성공하면 루프를 빠져나온다.
      await action
      return
    } catch (e) {
      // action이 실패하면 interval만큼 대기한 후 다시 시도한다.
      console.error(e)
      await sleep(interval)
    }
  }
}

export function getLocalDatabaseRandomKey(): string {
  return `created_${getRandomString(12, false)}`
}

function sleep(milliseconds: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, milliseconds))
}

export async function toBase64Webp(file: File): Promise<string | undefined> {
  const mimeType = file.type

  if (!mimeType.startsWith('image/')) {
    return undefined
  }

  return new Promise<string | undefined>(resolve => {
    const reader = new FileReader()

    reader.onload = (e: ProgressEvent<FileReader>): void => {
      const dataURL = e.target?.result as string

      if (mimeType === 'image/webp') {
        // 이미 webp 파일인 경우 base64로 변환만 해서 반환
        const base64 = dataURL.split(',')[1]
        resolve(base64)
      } else {
        // 이미지 파일을 webp로 변환
        const img = new Image()
        img.onload = (): void => {
          const canvas = document.createElement('canvas')
          canvas.width = img.width
          canvas.height = img.height

          const ctx = canvas.getContext('2d')
          if (ctx) {
            ctx.drawImage(img, 0, 0)
            const webpDataURL = canvas.toDataURL('image/webp', 0.8)
            resolve(webpDataURL)
          } else {
            resolve(undefined)
          }
        }
        img.onerror = (): void => {
          resolve(undefined)
        }
        img.src = dataURL
      }
    }

    reader.onerror = (): void => {
      resolve(undefined)
    }

    reader.readAsDataURL(file)
  })
}

export async function downloadUrl(_url: string, data: any): Promise<void> {
  try {
    const response = await axios({
      url: _url, // 서버의 파일 다운로드 엔드포인트
      method: 'POST',
      data,
      responseType: 'blob', // blob 형식으로 응답 받기
    })

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url

    // 파일 이름을 지정할 수 있습니다.
    link.setAttribute('download', 'filename.dwg') // 예: 'file.pdf'
    document.body.appendChild(link)
    link.click()

    // 다운로드 후 링크를 제거합니다.
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('파일 다운로드 중 오류 발생:', error)
  }
}

export function downloadFileObject(file: File): void {
  // `Blob`이나 `File` 객체로부터 URL을 생성
  const url = window.URL.createObjectURL(file)

  // 다운로드 링크 생성
  const link = document.createElement('a')
  link.href = url
  link.download = file.name // 파일 이름을 지정

  // 링크 클릭으로 다운로드 트리거
  document.body.appendChild(link)
  link.click()

  // 정리
  link.remove()
  window.URL.revokeObjectURL(url)
}

// webp to jpg
export async function convertWebpToJpg(webpBlob: Blob): Promise<Blob | null> {
  return new Promise(resolve => {
    const img = new Image()
    const blobUrl = URL.createObjectURL(webpBlob)

    img.onload = (): void => {
      try {
        const canvas = document.createElement('canvas')
        let { width, height } = img

        // 브라우저 Canvas 크기 제한 고려 (대부분 브라우저에서 32767px 제한)
        const MAX_CANVAS_SIZE = 32767
        if (width > MAX_CANVAS_SIZE || height > MAX_CANVAS_SIZE) {
          const scale = Math.min(MAX_CANVAS_SIZE / width, MAX_CANVAS_SIZE / height)
          width = Math.floor(width * scale)
          height = Math.floor(height * scale)
        }

        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d', {
          alpha: false, // JPEG는 투명도 없으므로 성능 향상
          willReadFrequently: false,
        })

        if (ctx) {
          ctx.imageSmoothingEnabled = true
          ctx.imageSmoothingQuality = 'high'

          // 투명한 부분을 흰색으로 처리하기 위해 배경을 흰색으로 채움
          ctx.fillStyle = '#FFFFFF'
          ctx.fillRect(0, 0, width, height)

          // 스케일링이 필요한 경우에만 적용
          if (width !== img.width || height !== img.height) {
            ctx.drawImage(img, 0, 0, width, height)
          } else {
            ctx.drawImage(img, 0, 0)
          }

          canvas.toBlob(
            blob => {
              URL.revokeObjectURL(blobUrl)
              resolve(blob)
            },
            'image/jpeg',
            0.95,
          )
        } else {
          URL.revokeObjectURL(blobUrl)
          resolve(null)
        }
      } catch (error) {
        console.error('이미지 변환 중 오류 발생:', error)
        URL.revokeObjectURL(blobUrl)
        resolve(null)
      }
    }

    img.onerror = (): void => {
      URL.revokeObjectURL(blobUrl)
      resolve(null)
    }

    img.src = blobUrl
  })
}
